import { inject, Injectable } from "@angular/core";
import { LocaleString } from "@grupo-constant/next-dtype/i18n";
import { BehaviorSubject, catchError, map, Observable, throwError } from "rxjs";
import { DTypeService } from "../dtype/dtype.service";
import { Net2BeepleMethodsEnum } from "../enums/net2beeple-methods.enum";
import { HttpService } from "../http/http.service";
import { RetrieveDTypeDataCollectionRequest, RetrieveLocationChangeRequest } from "../http/request.model";
import { CategoryNode, DownloadMasterXSSFBody, FlushMasterCategoryTreeReply, MASTER_URI, NextValueBody, NextValueReply, RetrieveMasterCategoryTreeReply, RetrieveMasterDataAsSelectOptionReply, UploadMasterXSSFBody, UploadMasterXSSFResponse } from "./master.types";
import { Net2BeepleLocator } from "../dtype/dtype.types";
import { DeleteDTypeData, DType, DTypeRepository, DTypeStatus, JsonReply, Pagination, SearchDTypeDataCollection } from "@grupo-constant/next-dtype/types";
import { cloneDeep } from "lodash-es";
import { RetrieveLocationChangeRequestReply } from "../http/reply.model";

@Injectable({
    providedIn: 'root'
})
export class MasterService {

    private dtypeService = inject(DTypeService);
    private httpService = inject(HttpService);

    private masterUri: BehaviorSubject<string> = new BehaviorSubject(undefined);
    private masterCaption: BehaviorSubject<LocaleString> = new BehaviorSubject(undefined);

    get masterUri$(): Observable<string> {
        return this.masterUri.asObservable();
    }

    get masterCaption$(): Observable<LocaleString> {
        return this.masterCaption.asObservable();
    }

    setMasterUri(uri: string) {
        this.masterUri.next(uri);
    }

    setMasterCaption(caption: LocaleString) {
        this.masterCaption.next(caption);
    }

    retrieveMasterDataDataCollection(body: RetrieveDTypeDataCollectionRequest) {
        return this.dtypeService.retrieveDTypeDataCollection(body);
    }

    retrieveMasterDataDTypeRepositoryByUri(uri: string, status: DTypeStatus, locator?: Net2BeepleLocator): Observable<DTypeRepository> {
        return this.dtypeService.retrieveDTypeRepositoryByUri({ uri, status, locator, bindableOptions: true });
    }

    retrieveMasterDataDtypeForm(values: any, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: MASTER_URI, status: DTypeStatus.DESIGN, bindableOptions: true }, values, options);
    }

    retrieveMasterDataDtypeFormFromDtype(dtype: DType, values?: any, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormFromDtype(dtype, values, options);
    }

    retrieveMasterDataAsSelectOptions(uri: string) {
        return this.httpService.post(Net2BeepleMethodsEnum.retrieveMasterDataAsSelectOption, { uri }).pipe(
            catchError((error) => {
                console.error(error);
                return throwError(() => error.error);
            }),
            map((reply: RetrieveMasterDataAsSelectOptionReply) => reply.selectOptions ?? [])
        );
    }

    deleteData(body: DeleteDTypeData) {
        return this.dtypeService.deleteDTypeData(body);
    }

    searchData(body: SearchDTypeDataCollection) {
        return this.dtypeService.searchDTypeDataCollection(body);
    }

    retrieveMasterCategoryTree(uri: string) {
        return this.httpService.post<RetrieveMasterCategoryTreeReply>(Net2BeepleMethodsEnum.retrieveMasterCategoryTree, { uri });
    }

    notifySystemChangesForUsers() {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.notifySystemChangesForUsers, {});
    }

    nextValue(body: NextValueBody) {
        return this.httpService.post<NextValueReply>(Net2BeepleMethodsEnum.nextValue, body);
    }

    getViewItems(items: any[], totalLength: number, page: number = 0, size: number = 10) {
        // Clone the list to prevent accidental updates
        let itemList = cloneDeep(items)

        // Paginate - Start

        // Calculate pagination details
        const begin = page * size;
        const end = Math.min((size * (page + 1)), totalLength);
        const lastPage = Math.max(Math.ceil(totalLength / size), 1);

        // Prepare the pagination object
        let pagination: Partial<Pagination>;

        // If the requested page number is bigger than
        // the last possible page number, return null for
        // tasks but also send the last possible page so
        // the app can navigate to there
        if (page > lastPage) {
            pagination = {
                lastPage
            };
        } else {
            // Paginate the results
            itemList = itemList.slice(begin, end);

            // Prepare the pagination
            pagination = {
                length: totalLength,
                size,
                page,
                lastPage: lastPage,
                startIndex: begin,
                endIndex: end - 1
            };
        }

        return { list: itemList, pagination };
    }

    flushMasterCategoryTree(uri: string, tree: CategoryNode[]) {
        return this.httpService.post<FlushMasterCategoryTreeReply>(Net2BeepleMethodsEnum.flushMasterCategoryTree, { uri, tree });
    }

    retrieveLocationChangeRequest(request: RetrieveLocationChangeRequest) {
        return this.httpService.post<RetrieveLocationChangeRequestReply>(Net2BeepleMethodsEnum.retrieveLocationChangeRequest, request);
    }

    flushLocationChangeResult(oid: string, approve: boolean) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.flushLocationChangeResult, { oid, approve });
    }

    getLocatorFromString(stringLocator: string): Net2BeepleLocator {
        return this.dtypeService.getLocatorFromString(stringLocator);
    }

    downloadMasterXSSF(body: DownloadMasterXSSFBody): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadMasterXSSF, body, 'blob');
    }

    downloadStructureXSSF(body: DownloadMasterXSSFBody): any {
        return this.httpService.postWithHeaders<any>(Net2BeepleMethodsEnum.downloadMasterFormStructureXSSF, body, 'blob');
    }

    uploadMasterXSSF(body: UploadMasterXSSFBody): Observable<UploadMasterXSSFResponse> {
        return this.httpService.post<UploadMasterXSSFResponse>(Net2BeepleMethodsEnum.uploadMasterXSSF, body);
    }
}
